/**
 * Error Reporter — sends frontend errors to backend for CloudWatch logging
 *
 * Features:
 * - Deduplicates identical errors within 60s window
 * - Batches errors to avoid excessive requests
 * - Fails silently to never break the UI
 * - Works outside React context (global error handlers)
 *
 * Usage:
 *   import { reportError, setErrorContext } from '@/lib/errorReporter';
 *
 *   // On login, set user context:
 *   setErrorContext({ userId: user.id, accountId: user.accountId });
 *
 *   // Report an error:
 *   reportError('component', error, { extra: 'data' });
 */

const ENDPOINT = "/api/logs/client";
const DEDUPE_WINDOW_MS = 60_000; // 60 seconds
const BATCH_DELAY_MS = 100; // Batch errors within 100ms

// ── State ────────────────────────────────────────────────────────────────────

/** User context (set on login, cleared on logout) */
let userContext = {
  userId: null,
  accountId: null,
};

/** Session ID (persists for tab lifetime) */
const sessionId = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

/** Recent error hashes for deduplication */
const recentErrors = new Map();

/** Pending errors to batch */
let pendingErrors = [];
let batchTimeout = null;

// ── Public API ───────────────────────────────────────────────────────────────

/**
 * Set user context for error reports.
 * Call on login, clear on logout.
 *
 * @param {{ userId?: string, accountId?: string }} ctx
 */
export function setErrorContext(ctx) {
  userContext = {
    userId: ctx?.userId ?? null,
    accountId: ctx?.accountId ?? null,
  };
}

/**
 * Clear user context (call on logout).
 */
export function clearErrorContext() {
  userContext = { userId: null, accountId: null };
}

/**
 * Report an error to the backend.
 * Deduplicates identical errors within 60s window.
 *
 * @param {string} source - Error source ('boundary', 'window.error', 'api', etc.)
 * @param {Error|unknown} error - The error object
 * @param {object} [extra] - Additional context (componentStack, filename, etc.)
 */
export function reportError(source, error, extra = {}) {
  try {
    const err = error instanceof Error ? error : new Error(String(error));
    const hash = computeHash(err, source);

    // Dedupe: skip if we've seen this exact error recently
    const lastSeen = recentErrors.get(hash);
    if (lastSeen && Date.now() - lastSeen < DEDUPE_WINDOW_MS) {
      return;
    }
    recentErrors.set(hash, Date.now());

    // Cleanup old hashes periodically
    if (recentErrors.size > 100) {
      const cutoff = Date.now() - DEDUPE_WINDOW_MS;
      for (const [key, ts] of recentErrors) {
        if (ts < cutoff) recentErrors.delete(key);
      }
    }

    // Queue for batching
    pendingErrors.push({
      level: "error",
      message: `[${source}] ${err.message}`,
      error: {
        name: err.name,
        message: err.message,
        stack: err.stack,
      },
      context: {
        source,
        url: window.location?.href,
        userAgent: navigator.userAgent,
        userId: userContext.userId,
        accountId: userContext.accountId,
        sessionId,
        timestamp: new Date().toISOString(),
        ...extra,
      },
    });

    // Schedule batch send
    if (!batchTimeout) {
      batchTimeout = setTimeout(flushErrors, BATCH_DELAY_MS);
    }
  } catch {
    // Never throw — error reporting should never break the app
  }
}

// ── Internal ─────────────────────────────────────────────────────────────────

function computeHash(error, source) {
  // Hash based on error message + first stack frame + source
  const firstFrame = error.stack?.split("\n")[1]?.trim() || "";
  return `${source}:${error.message}:${firstFrame}`;
}

async function flushErrors() {
  batchTimeout = null;
  if (pendingErrors.length === 0) return;

  const errors = pendingErrors;
  pendingErrors = [];

  // Send each error individually (backend expects single error per request)
  // Use Promise.allSettled to not fail on individual errors
  await Promise.allSettled(
    errors.map((payload) =>
      fetch(ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        // Don't include credentials — this is a public endpoint
        keepalive: true, // Ensure request completes even if page unloads
      }).catch(() => {
        // Ignore network errors — we can't report errors about reporting errors
      })
    )
  );
}

// ── Exports ──────────────────────────────────────────────────────────────────

export default { reportError, setErrorContext, clearErrorContext };
